import React, { useState, useContext, useEffect } from 'react';
import styled from 'styled-components';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { createGlobalStyle } from 'styled-components';
import { Flex, Box } from '@rebass/grid';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { navigate } from '@reach/router';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer';
import Avatar from '@material-ui/core/Avatar';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListSubheader from '@material-ui/core/ListSubheader';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Badge from '@material-ui/core/Badge';
import BannedUser from '@material-ui/icons/Block';
import get from 'lodash.get';
import Menu from '@material-ui/icons/Menu';
import CheckCircle from '@material-ui/icons/CheckCircle';
import Notifications from '@material-ui/icons/Notifications';
import PrivacyIcon from '@material-ui/icons/Lock';
import AccountCircle from '@material-ui/icons/AccountCircle';
import Family from '@material-ui/icons/People';
import Members from '@material-ui/icons/Group';
import DashBoard from '@material-ui/icons/Dashboard';
import FeedbackIcon from '@material-ui/icons/Feedback';
import TrendingUp from '@material-ui/icons/TrendingUp';
import VerifiedUser from '@material-ui/icons/VerifiedUser';
import Announcement from '@material-ui/icons/Announcement';
import { Link } from '@reach/router';
import { useUser } from '../lib/hooks/use-user';
import { usePendingFeedback } from '../lib/hooks/use-user-feedback';
import { useNewsFeedUnreadCount } from '../lib/hooks/use-news-feed-unread';
import { AppContext } from '../app';
import { useLocation } from '@reach/router';
import Cookies from 'js-cookie';
import { colors } from '../config/theme';

const GlobalStyle = createGlobalStyle`
  body {
    background-color: ${(props) => (props.whiteColor ? colors.surfaceContrast : colors.surface)};
    color: ${colors.textPrimary};
    a {
      color: ${colors.textPrimary};
      text-decoration: none;    
    };
    .wrapper-test {
        width: 100%;
        .firebaseui-list-item {
            button {
               width: 100%;
               max-width: 100%;
               height: 70px;
            }
        }
    }
}
`;

const PageWrapper = styled.div`
  width: 100%;
  top: 0;
  left: 0;
  color: 'white';
`;

const PageContent = styled.div`
  @media (max-width: 600px) {
    margin: ${(props) => (props.noMobileMargin ? '16px 0' : '16px auto')};
    width: ${(props) => (props.noMobileMargin ? '100%' : '90%')};
  }
  margin: 16px auto;
  width: 90%;
  max-width: ${(props) => (props.dt ? '1200px' : '700px')};
`;

const AppBar = styled(Flex)`
  width: 100%;
  height: 50px;
  color: ${colors.onPrimary};
  position: sticky;
  top: 0;
  left: 0;
  padding: 0 16px;
  background: ${colors.primary};
  justify-content: flex-start;
  align-items: center;
  z-index: 1000;

  /* GlobalStyle colours every anchor with the body text colour, which is dark on
     a light theme. The news feed and notification icons are the only header items
     wrapped in a Link, so without this they ignore the header's own colour. */
  a {
    color: inherit;
  }
`;

const SideMenuContent = styled.div`
  height: 100vh;
  width: 300px;
`;

const SubHeaderContent = styled.div`
  display: flex;
  padding: 16px 0px;
`;

const BottomContent = styled.div`
  padding: 16px;
`;

const NameContainer = styled.div`
  width: 100%;
  margin-left: 16px;
`;

const styles = {
  header: {
    background: colors.surface,
  },
  button: {
    width: '100%',
  },
  logout: {
    width: '100%',
  },
  name: {
    width: '90%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  notificationBadge: {
    backgroundColor: colors.dangerStrong,
    // The count sits on a filled red badge, so it follows the fill rather than
    // the page text colour.
    color: colors.onPrimary,
  },
};

const PageLayout = (props) => {
  const {
    leftButton,
    rightButton = () => null,
    bottomNav = () => null,
    pageTitle,
    logoTitle,
    children,
    classes,
    selected,
    dt,
    noMobileMargin = false,
  } = props;

  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [get(location, 'pathname')]);

  const { loggedInUserId } = useContext(AppContext);
  const { user } = useUser(loggedInUserId);

  const [drawer, setDrawer] = useState(false);

  const hamburgerMenu = () => {
    return <Menu onTouchStart={toggleMenu} onClick={toggleMenu} />;
  };

  const toggleMenu = () => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage('buttonPushed');
    }
    setDrawer((prevState) => !prevState);
  };

  const logOut = async () => {
    try {
      await firebase.auth().signOut();
      Cookies.remove('authToken');
      navigate('/login');
    } catch (e) {
      console.log('oops something went wrong');
    }
  };

  const permissions = get(user, 'permissions', []);
  const isAdmin = permissions.includes('admin');
  const isCoach = permissions.includes('coach');
  const isKiosk = permissions.includes('kiosk');
  const gymUUID = get(user, 'gym.uuid');
  const pathGymUidMatch = get(location, 'pathname', '').match(/gyms\/([^/]+)/);
  const effectiveGymUid =
    gymUUID || (pathGymUidMatch ? pathGymUidMatch[1] : undefined);
  const { pending: pendingFeedback = [] } = usePendingFeedback(effectiveGymUid);
  const { unreadCount: newsFeedUnreadCount } = useNewsFeedUnreadCount(
    effectiveGymUid,
    loggedInUserId,
  );

  return (
    <PageWrapper>
      <SwipeableDrawer open={drawer} onOpen={toggleMenu} onClose={toggleMenu}>
        <SideMenuContent>
          <List
            component="nav"
            subheader={
              <ListSubheader className={get(classes, 'header')}>
                <SubHeaderContent>
                  <Avatar src={get(user, 'avatar_url')}>TD</Avatar>
                  <NameContainer>
                    <Typography>{get(user, 'name')}</Typography>
                    <Typography className={classes.name}>
                      {get(user, 'email')}
                    </Typography>
                  </NameContainer>
                </SubHeaderContent>
              </ListSubheader>
            }
          >
            <Link to={`/profile/${get(user, 'uid')}`}>
              <ListItem button selected={selected === 'profile'}>
                <ListItemIcon>
                  <AccountCircle />
                </ListItemIcon>
                <ListItemText primary="Profile" />
              </ListItem>
            </Link>
            <Link to={`/profile/${get(user, 'uid')}/children`}>
              <ListItem button selected={selected === 'children'}>
                <ListItemIcon>
                  <Family />
                </ListItemIcon>
                <ListItemText primary="Family" />
              </ListItem>
            </Link>
            <Link to="/news-feed">
              <ListItem button selected={selected === 'news-feed'}>
                <ListItemIcon>
                  <Announcement />
                </ListItemIcon>
                <ListItemText primary="News Feed" />
              </ListItem>
            </Link>
            {isAdmin && effectiveGymUid && (
              <Link to={`/admin/dashboard/${effectiveGymUid}`}>
                <ListItem button selected={selected === 'dashboard'}>
                  <ListItemIcon>
                    <DashBoard />
                  </ListItemIcon>
                  <ListItemText primary="Dashboard" />
                </ListItem>
              </Link>
            )}
            {(isAdmin || isCoach) && effectiveGymUid && (
              <Link to={`/admin/gyms/${effectiveGymUid}/users`}>
                <ListItem button selected={selected === 'members'}>
                  <ListItemIcon>
                    <Members />
                  </ListItemIcon>
                  <ListItemText primary="Gym Members" />
                </ListItem>
              </Link>
            )}
            {isAdmin && effectiveGymUid && (
              <Link to={`/admin/gyms/${effectiveGymUid}/member-explorer`}>
                <ListItem button selected={selected === 'member-explorer'}>
                  <ListItemIcon>
                    <Members />
                  </ListItemIcon>
                  <ListItemText primary="Member Explorer" />
                </ListItem>
              </Link>
            )}
            {(isAdmin || isKiosk) && effectiveGymUid && (
              <Link to={`/admin/gyms/${effectiveGymUid}/kiosk`}>
                <ListItem button selected={selected === 'kiosk'}>
                  <ListItemIcon>
                    <CheckCircle />
                  </ListItemIcon>
                  <ListItemText primary="Kiosk Checkin" />
                </ListItem>
              </Link>
            )}
            {isAdmin && effectiveGymUid && (
              <Link to={`/admin/gyms/${effectiveGymUid}/disabled-users`}>
                <ListItem button selected={selected === 'disabled'}>
                  <ListItemIcon>
                    <BannedUser />
                  </ListItemIcon>
                  <ListItemText primary="Disabled Accounts" />
                </ListItem>
              </Link>
            )}
            {isAdmin && (
              <Link to={`/admin/gyms/${gymUUID}/user-permissions`}>
                <ListItem button selected={selected === 'User Permissions'}>
                  <ListItemIcon>
                    <VerifiedUser />
                  </ListItemIcon>
                  <ListItemText primary="User Permissions" />
                </ListItem>
              </Link>
            )}
            {isAdmin && effectiveGymUid && (
              <Link to={`/admin/gyms/${effectiveGymUid}/feedback-queue`}>
                <ListItem button selected={selected === 'feedback-queue'}>
                  <ListItemIcon>
                    <FeedbackIcon />
                  </ListItemIcon>
                  <ListItemText primary="Feedback Queue" />
                </ListItem>
              </Link>
            )}
            {isAdmin && effectiveGymUid && (
              <Link to={`/admin/gyms/${effectiveGymUid}/promotions`}>
                <ListItem button selected={selected === 'promotions'}>
                  <ListItemIcon>
                    <TrendingUp />
                  </ListItemIcon>
                  <ListItemText primary="Promotions" />
                </ListItem>
              </Link>
            )}
            <Link to={`/privacy`}>
              <ListItem button selected={selected === 'privacy'}>
                <ListItemIcon>
                  <PrivacyIcon />
                </ListItemIcon>
                <ListItemText primary="Privacy Policy" />
              </ListItem>
            </Link>
          </List>
        </SideMenuContent>
        <BottomContent>
          <Button
            onClick={logOut}
            className={get(classes, 'logout')}
            variant="outlined"
          >
            Logout
          </Button>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '10px',
            }}
          >
            <small>v 4.17.0</small>
          </div>
        </BottomContent>
      </SwipeableDrawer>
      <GlobalStyle />
      <AppBar>
        <Box width={1 / 4}>{leftButton ? leftButton : hamburgerMenu()}</Box>
        <Box width={1 / 2}>
          <Typography variant="h6" color="inherit" align="center">
            {pageTitle ? pageTitle : <Avatar src={logoTitle} />}
          </Typography>
        </Box>
        <Flex justifyContent="flex-end" width={1 / 4}>
          {effectiveGymUid && (
            <Link to="/news-feed">
              <IconButton aria-label="News Feed" color="inherit">
                <Badge
                  color="secondary"
                  badgeContent={newsFeedUnreadCount}
                  invisible={newsFeedUnreadCount === 0}
                  classes={{ badge: classes.notificationBadge }}
                >
                  <Announcement />
                </Badge>
              </IconButton>
            </Link>
          )}
          {isAdmin && effectiveGymUid && (
            <Link to={`/admin/gyms/${effectiveGymUid}/feedback-queue`}>
              <IconButton aria-label="Notifications" color="inherit">
                <Badge
                  color="secondary"
                  badgeContent={(pendingFeedback || []).length}
                  invisible={(pendingFeedback || []).length === 0}
                  classes={{ badge: classes.notificationBadge }}
                >
                  <Notifications />
                </Badge>
              </IconButton>
            </Link>
          )}
          {rightButton}
        </Flex>
      </AppBar>
      <PageContent noMobileMargin={noMobileMargin} dt={dt}>
        {children}
      </PageContent>
      {bottomNav()}
    </PageWrapper>
  );
};

export default withStyles(styles)(PageLayout);
